import * as LocalStorage from '@core/drivers/localStorage';

const localStorage = LocalStorage.driver;

export default class Settings {
	private namespace: string;

	/**
	 * Create a Settings namespaced instance.
	 *
	 * @param {string} namespace The namespace of settings to store.
	 */
	constructor(namespace?: string) {
		if (namespace) {
			this.namespace = namespace;
		}
	}

	/**
	 * Format the key with the namespace.
	 *
	 * @param {string} key The unnamespaced key of the setting.
	 * @returns {string}
	 * @private
	 */
	private _key(key: string): string {
		return this.namespace ? `${this.namespace}_${key}` : key;
	}

	/**
	 * Retrieve the setting key in localStorage.
	 *
	 * @param {string} key The unnamespaced key of the setting to retrieve.
	 * @returns {Promise<*, Error>}
	 */
	get<T = unknown>(key: string): Promise<T | null> {
		const value: string | null = localStorage.getItem(this._key(key));

		if (!value) {
			return Promise.resolve(null);
		}

		return new Promise<T>((resolve, reject) => {
			try {
				resolve(JSON.parse(value));
			} catch (e) {
				reject(e);
			}
		});
	}

	/**
	 * Store the key-value in settings.
	 *
	 * @param {string} key   The unnamespaced key of the setting to set.
	 * @param {*}      value The value to store in settings.
	 * @returns {Promise<void, Error>}
	 */
	set(key: string, value: unknown): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			try {
				localStorage.setItem(this._key(key), JSON.stringify(value));

				resolve();
			} catch (e) {
				reject(e);
			}
		});
	}

	/**
	 * Remove the key.
	 *
	 * @param {string} key The unnamespaced key of the setting to remove.
	 * @returns {Promise<void, Error>}
	 */
	remove(key: string): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			try {
				localStorage.removeItem(this._key(key));
				resolve();
			} catch (e) {
				reject(e);
			}
		});
	}
}

/**
 * A registry that holds multiple instances of {@link Settings}
 * indexed by {@link Settings.namespace}.
 */
export class SettingsManager {
    private static readonly defaultSettingsNamespace = 'archipad';
    private static readonly settingsByNamespace: Map<string, Settings> = new Map();

    public static getSettingsForLogin(login: string): Settings {
        const settingsNamespace = this.getNamespaceFromLogin(login);
        let settings = this.settingsByNamespace.get(settingsNamespace);

        if (settings === undefined) {
            settings = new Settings(settingsNamespace);
            this.settingsByNamespace.set(login, settings);
        }

        return settings;
    }

	public static getDefaultSettings():Settings {
		let settings = this.settingsByNamespace.get(this.defaultSettingsNamespace);

        if (settings === undefined) {
            settings = new Settings(this.defaultSettingsNamespace);
            this.settingsByNamespace.set(this.defaultSettingsNamespace, settings);
        }

		return settings;
	}

	/**
	 * Returns `logAs_${login}_archipad`
     */
	private static getNamespaceFromLogin(login:string):string {
		const settingsNamespace = `logAs_${login}_${this.defaultSettingsNamespace}`;
		return settingsNamespace;
	}
}